import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Camera,
  ArrowLeft,
  BookOpen,
  Calendar,
  Image,
  CreditCard,
  FileSignature,
  User,
  ChevronDown,
  Check,
  Droplet,
  Clock,
  Globe,
  Lock,
  Percent,
  Shield,
  Mail,
  Download,
  Upload,
  Settings,
  Zap,
  Bell,
  Receipt,
  Keyboard,
  ExternalLink,
  BarChart3,
  Star
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useAuth, API } from "../App";
import axios from "axios";
import SEO from "../components/SEO";

// Guide sections - Updated to reflect current system
const GUIDE_SECTIONS = [
  {
    id: "getting-started",
    title: "Getting Started",
    icon: <Zap className="w-5 h-5" />,
    color: "text-primary",
    bgColor: "bg-primary/10",
    content: [
      {
        title: "Creating Your Account",
        steps: [
          "Visit 360lumina.com and click \"Start Free Trial\"",
          "Sign in with Google (secure OAuth authentication)",
          "You'll start with a 7-day Pro trial - full access to all Pro features",
          "Complete your profile setup to get started"
        ]
      },
      {
        title: "Your Free Pro Trial",
        description: "Every new account starts with a 7-day Pro trial:",
        features: [
          { icon: <Check className="w-4 h-4" />, text: "Full access to Pro features during trial" },
          { icon: <Check className="w-4 h-4" />, text: "No credit card required to start" },
          { icon: <Check className="w-4 h-4" />, text: "Upgrade or downgrade anytime" },
          { icon: <Check className="w-4 h-4" />, text: "Keep your data if you switch plans" }
        ]
      },
      {
        title: "Your First Steps",
        steps: [
          "Complete your profile - Add your business name, bio, and services",
          "Upload a profile photo - Clients see this when booking",
          "Set your availability - Define when clients can book you",
          "Create a test gallery - Familiarize yourself with the delivery system"
        ]
      }
    ]
  },
  {
    id: "subscription",
    title: "Subscription & Plans",
    icon: <Receipt className="w-5 h-5" />,
    color: "text-amber-400",
    bgColor: "bg-amber-500/10",
    content: [
      {
        title: "Available Plans",
        table: [
          { section: "Starter ($19/mo)", desc: "Portfolio site, booking requests, calendar, basic galleries" },
          { section: "Pro ($39/mo)", desc: "Everything in Starter + photo sales, watermarking, promo codes, calendar sync, analytics" },
          { section: "Studio (Contact us)", desc: "Everything in Pro + contracts, team features, priority support" }
        ]
      },
      {
        title: "How Plan Changes Work",
        description: "Our subscription system uses a state machine to ensure smooth transitions:",
        features: [
          { icon: <Zap className="w-4 h-4" />, text: "Upgrades: Take effect immediately after payment confirmation" },
          { icon: <Clock className="w-4 h-4" />, text: "Downgrades: Scheduled for end of billing period (you keep features until then)" },
          { icon: <Shield className="w-4 h-4" />, text: "Plan Contract: Your features are locked in at time of subscription" }
        ],
        note: "Your 'effective tier' determines feature access. During a downgrade, you retain your current features until the billing period ends."
      },
      {
        title: "Changing Your Plan",
        steps: [
          "Go to Subscription in your dashboard",
          "Click 'Change Plan' to see available options",
          "Select your new plan and confirm",
          "Upgrades require payment; downgrades are scheduled"
        ]
      }
    ]
  },
  {
    id: "dashboard",
    title: "Dashboard Overview",
    icon: <Settings className="w-5 h-5" />,
    color: "text-blue-400",
    bgColor: "bg-blue-500/10",
    content: [
      {
        title: "Navigation Menu",
        table: [
          { section: "Overview", desc: "Quick stats: pending bookings, upcoming shoots, recent activity" },
          { section: "Requests", desc: "Manage booking inquiries and confirmations" },
          { section: "Calendar", desc: "View schedule, set availability, sync with Google Calendar (Pro+)" },
          { section: "Galleries", desc: "Create and manage client photo galleries" },
          { section: "Watermark", desc: "Configure watermark settings (Pro+)" },
          { section: "Analytics", desc: "View business insights and reports (Pro+)" },
          { section: "Payments", desc: "Transaction history and Stripe Connect status" },
          { section: "Invoices", desc: "Create and send professional invoices" },
          { section: "Promo Codes", desc: "Create discount codes (Pro+)" },
          { section: "Contracts", desc: "Manage contract templates (Studio)" },
          { section: "Subscription", desc: "Manage your 360Lumina plan" },
          { section: "Profile", desc: "Edit your public profile" }
        ]
      },
      {
        title: "Feature Gating",
        description: "Some features require higher-tier plans. You'll see lock icons on features not included in your current plan.",
        features: [
          { icon: <Lock className="w-4 h-4" />, text: "Locked features show which plan is required" },
          { icon: <Zap className="w-4 h-4" />, text: "Upgrade directly from any locked feature" },
          { icon: <Star className="w-4 h-4" />, text: "Trial users get full Pro access during trial" }
        ]
      }
    ]
  },
  {
    id: "bookings",
    title: "Managing Bookings",
    icon: <Calendar className="w-5 h-5" />,
    color: "text-green-400",
    bgColor: "bg-green-500/10",
    content: [
      {
        title: "Booking Flow",
        steps: [
          "Client visits your portfolio page",
          "They select a service type and preferred date/time",
          "You receive a notification (email + dashboard alert)",
          "Review the request and Confirm or Decline",
          "Client receives confirmation with next steps"
        ]
      },
      {
        title: "Booking Statuses",
        table: [
          { section: "Pending", desc: "New request awaiting your response" },
          { section: "Confirmed", desc: "Accepted and on your calendar" },
          { section: "Completed", desc: "Session finished" },
          { section: "Cancelled", desc: "Either party cancelled" },
          { section: "Declined", desc: "You declined the request" }
        ]
      }
    ]
  },
  {
    id: "calendar",
    title: "Calendar & Availability",
    icon: <Clock className="w-5 h-5" />,
    color: "text-yellow-400",
    bgColor: "bg-yellow-500/10",
    content: [
      {
        title: "Setting Availability",
        steps: [
          "Go to Calendar in your dashboard",
          "Click on a date to add available time slots",
          "Set start time, end time, and recurrence",
          "Clients can only book during available times"
        ]
      },
      {
        title: "Google Calendar Sync",
        badge: "Pro+",
        description: "Two-way sync prevents double bookings:",
        features: [
          { icon: <Download className="w-4 h-4" />, text: "Import: Google events show as blocked time" },
          { icon: <Upload className="w-4 h-4" />, text: "Export: Confirmed bookings sync to Google" },
          { icon: <Zap className="w-4 h-4" />, text: "Real-time: Changes sync automatically" }
        ]
      }
    ]
  },
  {
    id: "galleries",
    title: "Client Galleries",
    icon: <Image className="w-5 h-5" />,
    color: "text-purple-400",
    bgColor: "bg-purple-500/10",
    content: [
      {
        title: "Creating Galleries",
        steps: [
          "Go to Galleries → New Gallery",
          "Enter gallery name, client details, event type",
          "Choose visibility: Private (magic link) or Public",
          "Upload photos (drag-and-drop supported)"
        ]
      },
      {
        title: "Gallery Types",
        options: [
          { title: "Private Galleries", icon: <Lock className="w-4 h-4" />, desc: "Secure magic link access. Perfect for client deliveries." },
          { title: "Public Galleries", icon: <Globe className="w-4 h-4" />, desc: "Anyone with link can view. Great for events." }
        ]
      },
      {
        title: "Photo Downloads",
        badge: "Pro+",
        description: "Enable clients to purchase and download photos directly from galleries.",
        features: [
          { icon: <CreditCard className="w-4 h-4" />, text: "Set prices per photo or package" },
          { icon: <Download className="w-4 h-4" />, text: "Instant download after payment" },
          { icon: <Percent className="w-4 h-4" />, text: "Apply promo codes for discounts" }
        ]
      }
    ]
  },
  {
    id: "watermarking",
    title: "Watermarking",
    icon: <Droplet className="w-5 h-5" />,
    color: "text-cyan-400",
    bgColor: "bg-cyan-500/10",
    badge: "Pro+",
    content: [
      {
        title: "Watermark Setup",
        steps: [
          "Go to Watermark in your dashboard",
          "Enable watermarking",
          "Choose text or image watermark",
          "Configure position, opacity, and size"
        ]
      },
      {
        title: "Options",
        features: [
          { icon: <Check className="w-4 h-4" />, text: "Text watermark with custom font, color, position" },
          { icon: <Check className="w-4 h-4" />, text: "Image watermark (PNG with transparency recommended)" },
          { icon: <Check className="w-4 h-4" />, text: "9 position options (corners, edges, center)" },
          { icon: <Check className="w-4 h-4" />, text: "Adjustable opacity (10-100%)" }
        ],
        tip: "Watermark previews, deliver clean downloads after payment."
      }
    ]
  },
  {
    id: "analytics",
    title: "Analytics & Insights",
    icon: <BarChart3 className="w-5 h-5" />,
    color: "text-emerald-400",
    bgColor: "bg-emerald-500/10",
    badge: "Pro+",
    content: [
      {
        title: "Available Metrics",
        features: [
          { icon: <Check className="w-4 h-4" />, text: "Revenue tracking and trends" },
          { icon: <Check className="w-4 h-4" />, text: "Booking conversion rates" },
          { icon: <Check className="w-4 h-4" />, text: "Gallery engagement metrics" },
          { icon: <Check className="w-4 h-4" />, text: "Client acquisition sources" }
        ]
      },
      {
        title: "Accessing Analytics",
        description: "Analytics is available on Pro and Studio plans. Go to Dashboard → Analytics to view your business insights.",
        note: "If you're on a Starter plan, upgrade to Pro to unlock analytics."
      }
    ]
  },
  {
    id: "payments",
    title: "Payments & Invoicing",
    icon: <CreditCard className="w-5 h-5" />,
    color: "text-green-400",
    bgColor: "bg-green-500/10",
    content: [
      {
        title: "Stripe Connect Setup",
        steps: [
          "Go to Payments in your dashboard",
          "Click 'Connect with Stripe'",
          "Complete Stripe onboarding (business info, bank details)",
          "Start receiving payments directly to your account"
        ],
        note: "Payouts typically arrive within 2-3 business days."
      },
      {
        title: "Creating Invoices",
        steps: [
          "Go to Invoices → New Invoice",
          "Select client or enter details",
          "Add line items with amounts",
          "Apply promo codes if needed",
          "Send - client receives payment link via email"
        ]
      }
    ]
  },
  {
    id: "promo-codes",
    title: "Promo Codes",
    icon: <Percent className="w-5 h-5" />,
    color: "text-orange-400",
    bgColor: "bg-orange-500/10",
    badge: "Pro+",
    content: [
      {
        title: "Creating Promo Codes",
        steps: [
          "Go to Promo Codes → New Code",
          "Set code name (e.g., 'SUMMER20')",
          "Choose percentage or fixed discount",
          "Set optional limits: usage count, expiry date, minimum order"
        ]
      },
      {
        title: "Tracking",
        features: [
          { icon: <Check className="w-4 h-4" />, text: "Real-time usage statistics" },
          { icon: <Check className="w-4 h-4" />, text: "Revenue generated per code" },
          { icon: <Check className="w-4 h-4" />, text: "Enable/disable codes anytime" }
        ]
      }
    ]
  },
  {
    id: "contracts",
    title: "Contracts & E-Signatures",
    icon: <FileSignature className="w-5 h-5" />,
    color: "text-pink-400",
    bgColor: "bg-pink-500/10",
    badge: "Studio",
    content: [
      {
        title: "Contract Templates",
        description: "Create reusable contract templates for different services:",
        features: [
          { icon: <Check className="w-4 h-4" />, text: "Service agreements" },
          { icon: <Check className="w-4 h-4" />, text: "Model releases" },
          { icon: <Check className="w-4 h-4" />, text: "Custom templates with placeholders" }
        ]
      },
      {
        title: "Sending Contracts",
        steps: [
          "Select a template",
          "Enter client name and email",
          "Client receives link to review and sign",
          "Legally binding electronic signatures"
        ]
      }
    ]
  },
  {
    id: "profile",
    title: "Your Public Profile",
    icon: <User className="w-5 h-5" />,
    color: "text-indigo-400",
    bgColor: "bg-indigo-500/10",
    content: [
      {
        title: "Profile Settings",
        steps: [
          "Go to Profile in your dashboard",
          "Update business name, bio, services offered",
          "Upload profile photo and optional logo",
          "Add your location and contact info"
        ]
      },
      {
        title: "Your Portfolio URL",
        description: "Your public portfolio is available at:",
        note: "360lumina.com/photographer/[your-business-name]"
      }
    ]
  }
];

const PhotographerGuidePage = () => {
  const { user } = useAuth();
  const [expandedSection, setExpandedSection] = useState("getting-started");
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axios.get(`${API}/billing/plans`);
        setPlans(response.data.filter(p => ['starter', 'pro', 'studio'].includes(p.key)));
      } catch (error) {
        console.error("Failed to fetch plans:", error);
      }
    };
    fetchPlans();
  }, []);

  const renderContent = (item) => (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <h3 className="text-xl font-medium">{item.title}</h3>
        {item.badge && (
          <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
            {item.badge}
          </Badge>
        )}
      </div>

      {item.description && (
        <p className="text-muted-foreground">{item.description}</p>
      )}

      {item.steps && (
        <div className="space-y-2">
          {item.steps.map((step, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0 mt-0.5">
                {i + 1}
              </div>
              <span className="text-muted-foreground">{step}</span>
            </div>
          ))}
        </div>
      )}

      {item.table && (
        <div className="bg-muted/30 rounded-lg overflow-hidden">
          {item.table.map((row, i) => (
            <div key={i} className={`flex flex-col sm:flex-row sm:items-center gap-2 p-3 ${i > 0 ? 'border-t border-border' : ''}`}>
              <span className="font-medium text-primary sm:w-40 flex-shrink-0">{row.section}</span>
              <span className="text-muted-foreground text-sm">{row.desc}</span>
            </div>
          ))}
        </div>
      )}

      {item.features && (
        <div className="space-y-2">
          {item.features.map((f, i) => (
            <div key={i} className="flex items-start gap-3 text-sm">
              <span className="text-green-400 mt-0.5">{f.icon}</span>
              <span className="text-muted-foreground">{f.text}</span>
            </div>
          ))}
        </div>
      )}

      {item.options && (
        <div className="grid sm:grid-cols-2 gap-4">
          {item.options.map((opt, i) => (
            <Card key={i} className="bg-card border-border">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-primary">{opt.icon}</span>
                  <span className="font-medium">{opt.title}</span>
                </div>
                <p className="text-sm text-muted-foreground">{opt.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {item.note && (
        <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <Bell className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-200">{item.note}</p>
        </div>
      )}

      {item.tip && (
        <div className="flex items-start gap-3 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
          <Zap className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-yellow-200">{item.tip}</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="photographer-guide">
      <SEO 
        title="Photographer Guide | 360Lumina"
        description="Complete guide to running your photography business on 360Lumina."
      />

      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2 text-muted-foreground hover:text-white transition">
              <ArrowLeft className="w-4 h-4" />
              {user ? "Back to Dashboard" : "Back to Home"}
            </Link>
            {!user && (
              <Link to="/login">
                <Button>Start Free Trial</Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-12 md:py-16 px-6 border-b border-border">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Camera className="w-8 h-8 text-primary" />
            <span className="text-2xl font-serif">360Lumina</span>
          </div>
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 rounded-xl bg-primary/10">
              <BookOpen className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-serif">Photographer Guide</h1>
              <p className="text-muted-foreground">Everything you need to run your photography business</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <nav className="sticky top-24 space-y-1">
              <p className="text-sm font-medium text-muted-foreground mb-3 uppercase tracking-wider">Contents</p>
              {GUIDE_SECTIONS.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setExpandedSection(section.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                    expandedSection === section.id 
                      ? 'bg-primary/10 text-primary' 
                      : 'text-muted-foreground hover:bg-muted/50 hover:text-white'
                  }`}
                >
                  <span className={expandedSection === section.id ? section.color : ''}>{section.icon}</span>
                  <span className="truncate">{section.title}</span>
                  {section.badge && (
                    <Badge variant="outline" className="ml-auto text-xs flex-shrink-0">{section.badge}</Badge>
                  )}
                </button>
              ))}
            </nav>
          </aside>

          {/* Content */}
          <div className="lg:col-span-3 space-y-8">
            {GUIDE_SECTIONS.map((section) => (
              <motion.div
                key={section.id}
                initial={false}
                animate={{ opacity: expandedSection === section.id ? 1 : 0.5 }}
              >
                <Card className={`bg-card border-border overflow-hidden ${expandedSection === section.id ? 'ring-1 ring-primary/30' : ''}`}>
                  <CardHeader
                    className="cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-xl ${section.bgColor}`}>
                          <span className={section.color}>{section.icon}</span>
                        </div>
                        <div>
                          <CardTitle className="text-xl">{section.title}</CardTitle>
                          {section.badge && (
                            <Badge className="mt-1 bg-purple-500/20 text-purple-400 border-purple-500/30">
                              {section.badge}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${expandedSection === section.id ? 'rotate-180' : ''}`} />
                    </div>
                  </CardHeader>
                  
                  {expandedSection === section.id && (
                    <CardContent className="pt-0 space-y-8">
                      {section.content.map((item, idx) => (
                        <div key={idx} className={idx > 0 ? 'pt-6 border-t border-border' : ''}>
                          {renderContent(item)}
                        </div>
                      ))}
                    </CardContent>
                  )}
                </Card>
              </motion.div>
            ))}

            {/* Plan Comparison */}
            {plans.length > 0 && (
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-500/10">
                      <Receipt className="w-5 h-5 text-amber-400" />
                    </div>
                    Current Plan Pricing
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-3 gap-4">
                    {plans.map((plan, i) => (
                      <Card key={plan.key} className={`bg-card border-border ${i === 1 ? 'ring-2 ring-primary' : ''}`}>
                        <CardContent className="pt-6">
                          <h4 className="font-medium text-lg">{plan.name}</h4>
                          <p className="text-2xl font-bold text-primary mb-2">
                            {plan.show_price !== false ? `$${plan.monthly_price}/mo` : 'Contact us'}
                          </p>
                          {i === 1 && <Badge className="bg-primary/20 text-primary">Most Popular</Badge>}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  <div className="mt-4 text-center">
                    <Link to="/pricing">
                      <Button variant="outline">
                        View Full Comparison
                        <ExternalLink className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contact Support */}
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="pt-6 text-center">
                <Mail className="w-10 h-10 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-medium mb-2">Need help?</h3>
                <p className="text-muted-foreground mb-6">Our support team is here for you.</p>
                <a href="mailto:support@360lumina.com">
                  <Button size="lg">
                    <Mail className="w-4 h-4 mr-2" />
                    Contact Support
                  </Button>
                </a>
              </CardContent>
            </Card>

            <p className="text-center text-sm text-muted-foreground">
              Last updated: January 2026 • Version 2.2
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      {!user && (
        <footer className="border-t border-border py-8 px-6">
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-primary" />
              <span className="font-serif">360Lumina</span>
            </div>
            <div className="flex gap-6 text-sm text-muted-foreground">
              <Link to="/features" className="hover:text-white">Features</Link>
              <Link to="/pricing" className="hover:text-white">Pricing</Link>
              <Link to="/help" className="hover:text-white">Help</Link>
              <Link to="/login" className="hover:text-white">Login</Link>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default PhotographerGuidePage;
